// La tabla de la pestaña «todas las líneas»: una fila por línea, desplegable.
//
// POR QUÉ NO ES `TablaLineas` CON UN FLAG. Aquélla es el motor de las otras dos
// pestañas, que esta entrega no toca. Su fila es un `PedidoVentaLinea`, aquí es un
// `LineaAgrupada`; allí no hay expansión ni `colSpan` ni `<Fragment>`. Meter los dos
// modelos en el mismo componente obligaría a tocar `<colgroup>`, `<thead>` y el
// hueco inicial del pie —tres sitios que se alinean POR POSICIÓN y donde un
// descuadre no da error, sólo columnas corridas—. Lo que sí se comparte son las
// piezas: `SubTableWrapper`, `SubTotalsRow`, las marcas y `useAltoDisponible`.
//
// LAS MARCAS ATRAVIESAN LAS PESTAÑAS. La fila de aquí es la línea y allí es
// (línea × factura), así que las claves no coinciden. Marcar aquí significa «me
// interesa este artículo», no «me interesa esta factura», y el usuario espera verlo
// marcado al cambiar de pestaña: por eso se marcan TODAS las claves hijas, y el
// estado se lee de la primera. El mapeo vive en `lineasAgrupadas.clavesDeFilaDe`.
import { Fragment, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Chevron } from '@/components/common/Chevron'
import { SubTableWrapper } from '@/components/common/SubTableWrapper'
import { SubTotalsRow } from '@/components/common/SubTotalsRow'
import type { ColumnaPie } from '@/utils/subtotalesLineas'
import { FilaMarcable, MarcaCelda, CabeceraMarca } from '@/components/common/MarcasFilas'
import type { MarcasFilas } from '@/types'
import { clavesDeFilaDe, type LineaAgrupada } from '@/utils/lineasAgrupadas'
import { claveFila } from '@/utils/marcasFilas'
import type { ColumnaAgrupada, ColumnaHija } from './columnasAgrupadas'
import { useAltoDisponible } from './useAltoDisponible'
import { filaResaltada } from '../formato'
import { bordeTenueCls, tdCls, tenueCls, thCls } from './estilos'

export interface OrdenAgrupado {
  key: string
  dir: 'asc' | 'desc'
}

interface Props {
  dark: boolean
  grupos: LineaAgrupada[]
  columnas: ColumnaAgrupada[]
  columnasHijas: ColumnaHija[]
  totalSinFiltrar?: number
  orden: OrdenAgrupado | null
  onOrden: (key: string) => void
  /** Las líneas abiertas, por `inventtransid`. Vive en la sección para que el
   *  «desplegar todo» pueda gobernarlo, igual que en Cargas. */
  abiertas: Set<string>
  onAbrir: (inventtransid: string) => void
  coincide?: ((articulo?: string, descripcion?: string) => boolean) | null
  marcas: MarcasFilas
}

export function ordenarGrupos(
  grupos: LineaAgrupada[],
  columnas: ColumnaAgrupada[],
  orden: OrdenAgrupado | null,
): LineaAgrupada[] {
  if (!orden) return grupos
  const col = columnas.find(c => c.key === orden.key)
  if (!col?.valorOrden) return grupos
  const signo = orden.dir === 'asc' ? 1 : -1
  return [...grupos].sort((a, b) => {
    const va = col.valorOrden!(a)
    const vb = col.valorOrden!(b)
    if (va == null && vb == null) return 0
    if (va == null) return 1          // los huecos al final, ordene como ordene
    if (vb == null) return -1
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * signo
    return String(va).localeCompare(String(vb), 'es-ES') * signo
  })
}

export function TablaLineasAgrupadas({
  dark, grupos, columnas, columnasHijas, totalSinFiltrar, orden, onOrden,
  abiertas, onAbrir, coincide, marcas,
}: Props) {
  const { t } = useTranslation()
  const caja = useRef<HTMLDivElement>(null)
  const alto = useAltoDisponible(caja)

  const columnasPie: ColumnaPie<LineaAgrupada>[] = [
    { key: '__marca__' },   // hueco del canalón de marcas
    ...columnas.map(c => ({
      key: c.key,
      agg: c.agg ? { key: c.agg.key, value: c.agg.value } : undefined,
      format: c.formatoTotal,
    })),
  ]

  const th = thCls(dark)
  const td = tdCls(dark)
  const divisor = bordeTenueCls(dark)
  const fondoTh = dark ? 'bg-[#0d141f]' : 'bg-white'
  const fondoPie = dark ? '[&_td]:!bg-[#141d2b]' : '[&_td]:!bg-slate-100'
  const marco = dark ? 'border-white/8' : 'border-slate-200'
  const filaHover = dark ? 'hover:bg-white/[0.035]' : 'hover:bg-slate-50/80'
  // +1 por el canalón de marcas.
  const colSpanHijo = columnas.length + 1

  return (
    <div
      ref={caja}
      style={{ maxHeight: alto }}
      className={`overflow-auto scroll-thin rounded-lg border ${marco}`}
    >
      <table className="w-full min-w-max table-fixed border-collapse text-left">
        <colgroup>
          <col className="w-6" />
          {columnas.map(c => <col key={c.key} className={c.ancho} />)}
        </colgroup>

        <thead>
          <tr>
            <CabeceraMarca className={`${fondoTh} sticky top-0 z-10 border-b ${divisor}`} />
            {columnas.map(c => {
              const activa = orden?.key === c.key
              return (
                <th
                  key={c.key}
                  className={`${th} ${fondoTh} sticky top-0 z-10 border-b ${divisor} ${
                    c.alinear === 'right' ? 'text-right' : ''
                  }`}
                  aria-sort={activa ? (orden!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {c.valorOrden ? (
                    <button
                      type="button"
                      onClick={() => onOrden(c.key)}
                      className="inline-flex items-center gap-1 hover:underline"
                    >
                      {c.etiqueta}
                      {activa && <span aria-hidden="true">{orden!.dir === 'asc' ? '▲' : '▼'}</span>}
                    </button>
                  ) : c.etiqueta}
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody>
          {grupos.map(g => {
            const claves = clavesDeFilaDe(g)
            // La LÍNEA tiene clave propia, distinta de las de sus filas. Si
            // reusara la de la primera hija —que es lo que hacía—, marcar esa
            // sublínea encendería la línea entera sin que nadie entendiera por
            // qué, y sólo la primera. `marcasDeLinea` traduce esta clave a las de
            // sus hijas al leer y al escribir.
            const clave = claveFila(g.inventtransid)
            const abierta = abiertas.has(g.inventtransid)
            return (
              <Fragment key={g.inventtransid}>
                <FilaMarcable
                  marcas={marcas}
                  clave={clave}
                  dark={dark}
                  className={`border-t ${divisor} transition-colors duration-150 ${filaHover} ${
                    coincide?.(g.cabecera.articulo, g.cabecera.descripcion) ? filaResaltada(dark) : ''
                  }`}
                >
                  <MarcaCelda marcas={marcas} clave={clave} dark={dark} densa />
                  {columnas.map((c, i) => (
                    <td
                      key={c.key}
                      className={`${td} ${c.alinear === 'right' ? 'text-right tabular-nums' : ''}`}
                    >
                      {/* El control va DENTRO de la primera celda de datos y no en
                          un canalón propio: un canalón nuevo obligaría a tocar
                          colgroup, thead y el pie, que se alinean por posición. */}
                      {i === 0 ? (
                        <span className="flex items-center gap-1">
                          {g.tieneDesglose ? (
                            <button
                              type="button"
                              onClick={() => onAbrir(g.inventtransid)}
                              aria-expanded={abierta}
                              aria-label={t(abierta ? 'common.collapse' : 'common.expand')}
                              className={`shrink-0 ${tenueCls(dark)} hover:opacity-80`}
                            >
                              <Chevron abierto={abierta} />
                            </button>
                          ) : (
                            // Hueco del mismo ancho: sin él las filas sin desglose
                            // quedan corridas respecto a las que sí lo tienen.
                            <span className="w-3 shrink-0" aria-hidden="true" />
                          )}
                          {c.render(g)}
                        </span>
                      ) : c.render(g)}
                    </td>
                  ))}
                </FilaMarcable>

                {abierta && (
                  <SubTableWrapper dark={dark} colSpan={colSpanHijo}>
                    <div className="pl-9 pr-3 py-2 overflow-x-auto">
                      <table className="w-full min-w-max border-collapse text-left">
                        <thead>
                          <tr>
                            <CabeceraMarca />
                            {columnasHijas.map(c => (
                              <th
                                key={c.key}
                                className={`${th} ${c.alinear === 'right' ? 'text-right' : ''}`}
                              >
                                {c.etiqueta}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {g.filas.map((f, i) => (
                            // La sublínea se marca con la MISMA clave que en las
                            // otras dos pestañas: es la misma fila vista desde
                            // otro sitio, así que marcarla aquí la deja marcada
                            // allí. Y la línea entera sólo se enciende cuando lo
                            // están todas sus hijas.
                            <FilaMarcable
                              key={`${f.factura ?? ''}-${i}`}
                              marcas={marcas}
                              clave={claves[i] ?? ''}
                              dark={dark}
                              className={`border-t ${divisor}`}
                            >
                              <MarcaCelda marcas={marcas} clave={claves[i] ?? ''} dark={dark} densa />
                              {columnasHijas.map(c => (
                                <td
                                  key={c.key}
                                  className={`${td} ${c.alinear === 'right' ? 'text-right tabular-nums' : ''}`}
                                >
                                  {c.render(f)}
                                </td>
                              ))}
                            </FilaMarcable>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </SubTableWrapper>
                )}
              </Fragment>
            )
          })}
        </tbody>

        <tfoot className={`[&_td]:sticky [&_td]:bottom-0 [&_td]:z-10 ${fondoPie}`}>
          <SubTotalsRow
            dark={dark}
            rows={grupos}
            columnas={columnasPie}
            totalSinFiltrar={totalSinFiltrar}
          />
        </tfoot>
      </table>

      {grupos.length === 0 && (
        <p className={`px-3 py-8 text-center text-[12px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
          {t('common.noResults')}
        </p>
      )}
    </div>
  )
}
